import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { useListWorkingHours } from 'src/features/workingHours/api/useListWorkingHours';
import CreateWorkingHourFormModal from 'src/features/workingHours/components/form/CreateWorkingHoursFormModal';
import CellWorkingHoursActions from 'src/features/workingHours/components/table/CellWorkingHoursActions';
import { DAY_OF_WEEK, WorkingHours as WorkingHoursType } from 'src/features/workingHours/utils/types';
import { DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/workingHours/utils/constants';
import { workingHoursMessages } from 'src/features/workingHours/utils/messages';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTableControls } from 'src/registry/components/table/DataTableControls';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import { PERMISSION } from 'src/features/auth';
import useHasPermissions from 'src/hooks/useHasPermissions';

const sortingOptions = {
    initialSortingState: [
        { id: 'day_of_week', desc: false },
        { id: 'start_time', desc: false },
    ],
};
const searchOptions = { globalFilterKey: 'name' };

const columnHelper = createColumnHelper<WorkingHoursType>();

type WorkingHoursProps = {
    siteId: string;
};

function WorkingHours({ siteId }: WorkingHoursProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const manageSites = useHasPermissions([PERMISSION.MANAGE_SITES]);

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);

    const payload = React.useMemo(
        () => ({
            siteId,
            parkingId,
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
        }),
        [siteId, queryFiltersOptions, querySearchOptions, querySortingOptions, parkingId],
    );

    const { data, isLoading } = useListWorkingHours(payload);

    const weekDayOptions = React.useMemo(
        () =>
            Object.values(DAY_OF_WEEK).map((value) => ({
                label: formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[value]),
                value,
            })),
        [formatMessage],
    );

    const columns = React.useMemo(
        () => [
            columnHelper.accessor('zone', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) =>
                    row.original.zone.translations.find((translation) => translation.language === locale)?.name,
                meta: {
                    title: formatMessage(workingHoursMessages.zone),
                },
            }),
            columnHelper.accessor('day_of_week', {
                header: DataTableColumnHeader,
                enableSorting: true,
                cell: ({ row }) => formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[row.original.day_of_week]),
                enableColumnFilter: true,
                enableHiding: true,
                meta: {
                    inputType: INPUT_TYPE.MULTI_SELECT,
                    title: formatMessage(workingHoursMessages.weekday),
                    options: weekDayOptions,
                },
            }),
            columnHelper.accessor('all_day', {
                header: DataTableColumnHeader,
                cell: CellBoolean,
                enableSorting: true,
                meta: {
                    title: formatMessage(workingHoursMessages.allDay),
                },
            }),
            columnHelper.accessor('start_time', {
                header: DataTableColumnHeader,
                enableSorting: true,
                meta: {
                    title: formatMessage(workingHoursMessages.startTime),
                },
            }),
            columnHelper.accessor('end_time', {
                header: DataTableColumnHeader,
                enableSorting: true,
                meta: {
                    title: formatMessage(workingHoursMessages.endTime),
                },
            }),
            ...(manageSites
                ? [
                      columnHelper.display({
                          id: 'actions',
                          header: DataTableColumnHeader,
                          cell: CellWorkingHoursActions,
                      }),
                  ]
                : []),
        ],
        [formatMessage, locale, weekDayOptions, manageSites],
    );

    return (
        <DataTable<WorkingHoursType, any>
            isLoading={isLoading}
            columns={columns}
            data={data}
            search={search}
            filters={filters}
            sorting={sorting}
            semantics={{
                title: formatMessage(workingHoursMessages.workingHours),
                entryName: formatMessage(workingHoursMessages.workingHours).toLowerCase(),
                entryNames: formatMessage(workingHoursMessages.workingHours).toLowerCase(),
                placeholder: formatMessage(workingHoursMessages.workingHours),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageSites && { newEntry: CreateWorkingHourFormModal }),
            }}
            enableSearchParams={false}
        />
    );
}

export default WorkingHours;
